/*
 * Duration Utility Module
 * 
 * Provides utilities for converting, formatting, and parsing duration values
 * supporting both hours and minutes representation.
 * 
 * Part of duration-minutes-support feature
 */

/**
 * Convert hours and minutes to total minutes
 * 
 * @param {number} hours - Number of hours (non-negative integer)
 * @param {number} minutes - Number of minutes (0-59)
 * @returns {number} Total minutes
 * 
 * @example
 * convertToMinutes(2, 30) // returns 150
 * convertToMinutes(1, 0)  // returns 60
 * convertToMinutes(0, 45) // returns 45
 */
export const convertToMinutes = (hours, minutes) => {
  const h = Number(hours) || 0;
  const m = Number(minutes) || 0;
  return h * 60 + m;
};

/**
 * Convert total minutes to hours and minutes object
 * 
 * @param {number} totalMinutes - Total minutes to convert
 * @returns {{hours: number, minutes: number}} Object with hours and minutes
 * 
 * @example
 * convertToHoursAndMinutes(150) // returns { hours: 2, minutes: 30 }
 * convertToHoursAndMinutes(60)  // returns { hours: 1, minutes: 0 }
 * convertToHoursAndMinutes(45)  // returns { hours: 0, minutes: 45 }
 */
export const convertToHoursAndMinutes = (totalMinutes) => {
  const total = Number(totalMinutes) || 0;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return { hours, minutes };
};

/**
 * Format duration in minutes to human-readable string
 * 
 * Formatting rules:
 * - Minutes only if < 60 minutes: "45 minutes"
 * - Hours only if exact hour: "2 hours"
 * - Both if mixed: "2 hours 30 minutes"
 * - Abbreviated format: "2h 30m"
 * - Singular forms for value of 1: "1 hour" not "1 hours"
 * 
 * @param {number} totalMinutes - Total minutes to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.abbreviated - Use abbreviated format (default: false)
 * @returns {string} Formatted duration string
 * 
 * @example
 * formatDuration(150) // returns "2 hours 30 minutes"
 * formatDuration(60)  // returns "1 hour"
 * formatDuration(45)  // returns "45 minutes"
 * formatDuration(150, { abbreviated: true }) // returns "2h 30m"
 * formatDuration(0)   // returns "0 minutes"
 */
export const formatDuration = (totalMinutes, options = {}) => {
  const { abbreviated = false } = options;
  
  const total = Number(totalMinutes) || 0;
  
  // Handle zero or invalid duration
  if (total === 0) {
    return abbreviated ? '0m' : '0 minutes';
  }
  
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  
  // Minutes only (less than 1 hour)
  if (hours === 0) {
    if (abbreviated) {
      return `${minutes}m`;
    }
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  
  // Hours only (exact hour boundary)
  if (minutes === 0) {
    if (abbreviated) {
      return `${hours}h`;
    }
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  
  // Both hours and minutes
  if (abbreviated) {
    return `${hours}h ${minutes}m`;
  }
  
  const hoursText = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const minutesText = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  return `${hoursText} ${minutesText}`;
};

/**
 * Parse a formatted duration string back to total minutes
 * 
 * Supports various formats:
 * - "2 hours 30 minutes"
 * - "2h 30m"
 * - "45 minutes"
 * - "2 hours"
 * - "45m"
 * - "2h"
 * 
 * @param {string} formattedString - Formatted duration string
 * @returns {number|null} Total minutes, or null if parsing fails
 * 
 * @example
 * parseDuration("2 hours 30 minutes") // returns 150
 * parseDuration("2h 30m")             // returns 150
 * parseDuration("45 minutes")         // returns 45
 * parseDuration("invalid")            // returns null
 */
export const parseDuration = (formattedString) => {
  if (!formattedString || typeof formattedString !== 'string') {
    return null;
  }
  
  const str = formattedString.trim().toLowerCase();
  
  if (!str) {
    return null;
  }
  
  let hours = 0;
  let minutes = 0;
  
  // Match abbreviated format: "2h 30m", "2h", "30m"
  const abbreviatedMatch = str.match(/(?:(\d+)h)?(?:\s*(\d+)m)?/);
  if (abbreviatedMatch && (abbreviatedMatch[1] || abbreviatedMatch[2])) {
    hours = Number(abbreviatedMatch[1]) || 0;
    minutes = Number(abbreviatedMatch[2]) || 0;
    return hours * 60 + minutes;
  }
  
  // Match full format: "2 hours 30 minutes", "2 hours", "30 minutes"
  const hoursMatch = str.match(/(\d+)\s*hours?/);
  const minutesMatch = str.match(/(\d+)\s*minutes?/);
  
  if (hoursMatch || minutesMatch) {
    hours = hoursMatch ? Number(hoursMatch[1]) : 0;
    minutes = minutesMatch ? Number(minutesMatch[1]) : 0;
    return hours * 60 + minutes;
  }
  
  // No valid format found
  return null;
};

/**
 * Validate hours input
 * 
 * @param {any} hours - Hours value to validate
 * @returns {boolean} True if valid (non-negative integer)
 */
export const validateHours = (hours) => {
  const num = Number(hours);
  return Number.isInteger(num) && num >= 0;
};

/**
 * Validate minutes input
 * 
 * @param {any} minutes - Minutes value to validate
 * @returns {boolean} True if valid (integer between 0 and 59)
 */
export const validateMinutes = (minutes) => {
  const num = Number(minutes);
  return Number.isInteger(num) && num >= 0 && num <= 59;
};

/**
 * Get effective duration in minutes from a chapter/content object
 * Missing durationType means HOURS for legacy chapter content
 * 
 * @param {Object} item - Chapter or content item
 * @returns {number} Duration in minutes
 */
export const getEffectiveDurationInMinutes = (item) => {
  if (!item) return 0;

  const durationType = String(item.durationType || 'HOURS').toUpperCase();

  if (item.durationInHours != null && item.durationInHours > 0) {
    const duration = Number(item.durationInHours);
    return durationType === 'MINUTES' ? duration : duration * 60;
  }

  if (item.durationInMinutes != null && item.durationInMinutes > 0) {
    return Number(item.durationInMinutes);
  }
  
  return 0;
};
